import React, { Component } from 'react';
import { bool, string, object } from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import get from 'lodash.get';

import storage from 'dibs-browser-storage';

import Dropdown, { alignConstants } from '../Dropdown/Dropdown';
import DropdownBody from '../Dropdown/DropdownBody';
import DropdownLoadingContent from '../Dropdown/DropdownLoadingContent';
import NotificationsIcon from './NotificationsIcon';
import {
    setPromoCodeNotificationShown,
    wasPromoCodeNotificationShown,
} from '../../utils/promoCode.es';
import { getNotificationName, trackNotification } from './notificationTracking';

const BATCH_SIZE = 5;
const LOCAL_STORAGE_KEY_NAME = 'NAV_NOTIFICATIONS_DROPDOWN_LAST_OPEN_TIME';

const resetState = () => ({
    isOpen: false,
    fetchingNotifications: true,
    first: BATCH_SIZE,
});
const stopFetching = (isMore = false) => {
    const defaultReturn = { fetchingNotifications: false };
    return isMore ? Object.assign({}, defaultReturn) : defaultReturn;
};
const startFetching = () => ({ fetchingNotifications: true });
const openDropdown = () => ({ isOpen: true });
const hasNewNotification = (lastUnreadMessageDate, lastUnreadNotificationDate) => {
    const { localStorage } = storage;

    const mostRecentMessageReceivedAt = lastUnreadMessageDate
        ? new Date(lastUnreadMessageDate).getTime()
        : 0;
    const mostRecentNotificationReceivedAt = lastUnreadNotificationDate
        ? new Date(lastUnreadNotificationDate).getTime()
        : 0;

    const mostRecent = Math.max(mostRecentMessageReceivedAt, mostRecentNotificationReceivedAt);
    const lastOpen = Number(localStorage.getItem(LOCAL_STORAGE_KEY_NAME)) || 0;
    return mostRecent > lastOpen;
};
const isNewNotification = eventDate =>
    new Date(eventDate).getTime() > Number(localStorage.getItem(LOCAL_STORAGE_KEY_NAME)) || 0;
const trackNotificationAlerts = notifications => {
    notifications.forEach(notification => {
        const { eventDate, type } = notification;
        if (type === 'TRADE_REWARD') {
            return;
        }

        if (isNewNotification(eventDate)) {
            trackNotification(getNotificationName(type), 'alert displayed');
        }
    });
};

class Notifications extends Component {
    constructor(props) {
        super(props);
        this.state = {
            ...resetState(),
            AsyncComponents: null,
        };

        this.loadNotifications = this.loadNotifications.bind(this);
        this.onMouseEnterIcon = this.onMouseEnterIcon.bind(this);
        this.loadComponents = this.loadComponents.bind(this);
        this.lastReadTime = 0;
    }

    componentDidMount() {
        this.lastReadTime = Number(localStorage.getItem(LOCAL_STORAGE_KEY_NAME)) || 0;
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.user && this.props.user) {
            const feed = get(this.props, 'user.activityFeed.edges');
            if (Array.isArray(feed)) {
                trackNotificationAlerts(feed.map(edge => edge.node));
            }
        }
    }

    loadNotifications() {
        const { localStorage } = storage;
        const { hasRootPendingRefetch, relay, userId, user } = this.props;
        const fetchNotificationVariables = {
            fetchNotifications: true,
            userId,
            hasUserId: !!userId,
        };

        if (hasRootPendingRefetch) {
            return;
        }

        const refetchVariables = fragmentVariables =>
            Object.assign({}, fragmentVariables, fetchNotificationVariables);
        const refetchCallback = () => {
            localStorage.setItem(LOCAL_STORAGE_KEY_NAME, Date.now());
            if (get(user, 'activePromotion')) {
                setPromoCodeNotificationShown(get(user, 'activePromotion'));
            }
            this.setState(stopFetching);
        };

        this.setState(startFetching, () =>
            relay.refetch(refetchVariables, null, refetchCallback, { force: true })
        );
    }

    onMouseEnterIcon(e) {
        e.stopPropagation();
        if (!this.state.isOpen) {
            this.loadNotifications();
            this.setState(openDropdown);
        }
    }

    loadComponents() {
        import(/* webpackChunkName: "NotificationsBodyComponents" */
        './NotificationsBodyComponents').then(AsyncComponents => {
            this.setState({ AsyncComponents });
        });
    }

    render() {
        const { hasUserId, isClient, user } = this.props;
        const { fetchingNotifications, AsyncComponents } = this.state;
        const conversationMetadata = get(this.props, 'initialViewer.conversations');
        const hasConversations = !!conversationMetadata;

        if (!isClient || (hasUserId && !hasConversations) || !hasUserId || !user) {
            return null;
        }

        const activePromotion = user.activePromotion;
        const conversations = get(this.props, 'viewer.conversations') || null;
        const feed = get(this.props, 'user.activityFeed') || null;
        const totalUnreadConversationCount = get(
            conversationMetadata,
            'metadata.totalUnreadConversationCount'
        );

        const lastUnreadMessageDate = get(conversationMetadata, 'metadata.lastUnreadMessageDate');
        const lastUnreadNotificationDate = get(feed, 'edges[0].node.eventDate');
        const hasNew = hasNewNotification(lastUnreadMessageDate, lastUnreadNotificationDate);

        return (
            <Dropdown
                hasDropdownHeader
                align={alignConstants.ALIGN_RIGHT}
                onShow={this.loadComponents}
                onHide={() => {
                    this.setState(resetState);
                }}
            >
                <NotificationsIcon
                    onMouseEnter={this.onMouseEnterIcon}
                    hasUnread={
                        hasNew ||
                        (activePromotion && !wasPromoCodeNotificationShown(activePromotion))
                    }
                />
                <DropdownBody>
                    {AsyncComponents ? (
                        <React.Fragment>
                            <AsyncComponents.ActivityFeed
                                feed={feed}
                                lastReadTime={this.lastReadTime}
                                activePromotion={activePromotion}
                                tradeRewardEnabled={get(this.props, 'initialViewer')}
                            />
                            <AsyncComponents.Conversations
                                conversations={conversations}
                                totalUnread={totalUnreadConversationCount}
                                isLoading={fetchingNotifications}
                            />
                        </React.Fragment>
                    ) : (
                        <DropdownLoadingContent />
                    )}
                </DropdownBody>
            </Dropdown>
        );
    }
}

Notifications.propTypes = {
    hasUserId: bool,
    hasRootPendingRefetch: bool,
    isClient: bool,
    relay: object,
    userId: string,
    initialViewer: object,
    viewer: object,
    user: object,
};

export default createRefetchContainer(
    Notifications,
    {
        initialViewer: graphql`
            fragment Notifications_initialViewer on Viewer {
                conversations(filter: "showUnreadOnly") @include(if: $hasUserId) {
                    metadata {
                        lastUnreadMessageDate
                        totalUnreadConversationCount
                    }
                }
                ...ActivityFeed_tradeRewardEnabled
            }
        `,
        viewer: graphql`
            fragment Notifications_viewer on Viewer
                @argumentDefinitions(
                    first: { type: "Int", defaultValue: 5 }
                    fetchNotifications: { type: "Boolean!", defaultValue: false }
                ) {
                conversations(filter: "showUnreadOnly", first: $first, notifications: true)
                    @include(if: $fetchNotifications) {
                    ...Conversations_conversations
                }
            }
        `,
        user: graphql`
            fragment Notifications_user on User {
                activePromotion {
                    serviceId
                    code
                    flatAdjustmentRate {
                        rate {
                            currency
                            amount
                        }
                    }
                    percentAdjustmentRate {
                        rate
                    }
                }
                activityFeed(first: 3, elementTypes: [PENDING_FEEDBACK, TRADE_REWARD]) {
                    edges {
                        node {
                            type
                            eventDate
                        }
                    }
                    ...ActivityFeed_feed
                }
            }
        `,
    },
    graphql`
        query NotificationsRefetchQuery($first: Int, $fetchNotifications: Boolean!) {
            viewer {
                ...Notifications_viewer
                    @arguments(first: $first, fetchNotifications: $fetchNotifications)
            }
        }
    `
);
